import { DateTimeFormats } from "./constants";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";
import UTC from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(UTC);
dayjs.extend(timezone);

type DateType = Date | string | undefined;

export function isLeapYear(year: string): boolean {
  const regex = /^\d{4}$/i;
  let result = regex.test(year);
  if (result) {
    const intYear = parseInt(year);
    result = (intYear % 4 == 0 && intYear % 100 != 0) || intYear % 400 == 0;
  } else {
    throw new Error(`Invalid year passed-${year}`);
  }
  return result;
}

/*
  Allowed formats: HH:mm:ss and HH:mm.
  Otherwise, time will be set to 00:00:00
*/

export function convertTimeStringToDate(timeString: string | undefined): Date {
  const date = new Date();

  if (
    !timeString ||
    (!DateTimeFormats.HHmmss.exec(timeString) &&
      !DateTimeFormats.HHmm.exec(timeString))
  ) {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    return date;
  }

  const timeArray = timeString.split(":");
  const hours = parseInt(timeArray[0]);
  const minutes = parseInt(timeArray[1]);
  const seconds = timeArray[2] ? parseInt(timeArray[2]) : 0;
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);

  return date;
}

/*
  Allowed formats: yyyy-MM-dd and MM-dd.
  Otherwise, current date will be returned.
*/

export function convertDateStringToDate(dateString: string | undefined): Date {
  const date = new Date();

  if (dateString?.match(DateTimeFormats.yyyyMMdd)) {
    const dateArray = dateString.split("-");
    date.setFullYear(parseInt(dateArray[0]));
    date.setMonth(parseInt(dateArray[1]) - 1);
    date.setDate(parseInt(dateArray[2]));
  } else if (dateString?.match(DateTimeFormats.MMdd)) {
    const dateArray = dateString.split("-");
    date.setMonth(parseInt(dateArray[0]) - 1);
    date.setDate(parseInt(dateArray[1]));
  }

  return date;
}

/*
  Returns date object without time
*/

export function dateWithoutTime(date: Date): Date {
  const day = `${date.getDate()}`.padStart(2, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const year = `${date.getFullYear()}`;

  return new Date(`${year}-${month}-${day}`);
}

/*
  Calculates the number of days between two dates.
*/

export function daysBetweenDates(date1: Date, date2: Date): number {
  const differenceInTime = Math.abs(
    dateWithoutTime(date1).getTime() - dateWithoutTime(date2).getTime(),
  );
  return differenceInTime / (1000 * 3600 * 24);
}

/*
  Returns date string in yyyy-MM-dd format
*/

export function dateStringYYYYMMdd(date?: any): string {
  if (!date || typeof date !== `object`) {
    return "";
  }
  const day = `${date.getDate()}`.padStart(2, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const year = `${date.getFullYear()}`;

  return `${year}-${month}-${day}`;
}

/*
  Returns date string in yyyy-MM-ddTHH:mm format
*/

export function dateStringYYYYMMddHHmm(date?: Date): string {
  if (!date) {
    return "";
  }
  const day = `${date.getDate()}`.padStart(2, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const year = `${date.getFullYear()}`;
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function getDay(day: number) {
  switch (day) {
    case 0:
      return "Mon";
    case 1:
      return "Tue";
    case 2:
      return "Wed";
    case 3:
      return "Thu";
    case 4:
      return "Fri";
    case 5:
      return "Sat";
    case 6:
      return "Sun";
    default:
      return "";
  }
}

export function dateFilter(date: any, format = "DD/MM/YYYY HH:mm A") {
  return dayjs(date).format(format);
}

export function getDateString(
  diffDays: any,
  hrs: any,
  oldDate: any,
  ampm: any,
) {
  const arr2 = dayjs()
    .subtract(Math.floor(diffDays), "day")
    .format("MMM D, YYYY")
    .split(", ");
  const arr1 = arr2[0].split(" ");
  const nth = function (d: any) {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${arr1[1]}${nth(arr1[1])} ${arr1[0]} ${hrs}:${
    oldDate.getMinutes().toString().length == 1
      ? "0" + oldDate.getMinutes()
      : oldDate.getMinutes()
  } ${ampm}`;
}

export function getDayStringfromDifference(
  diff: any,
  hrs: any,
  oldDate: any,
  ampm: any,
) {
  const arr = dayjs()
    .subtract(Math.floor(diff), "day")
    .format("ddd, MMM D, YYYY")
    .split(", ");
  return `${arr[0]} ${hrs}:${
    oldDate.getMinutes().toString().length == 1
      ? "0" + oldDate.getMinutes()
      : oldDate.getMinutes()
  }${ampm}`;
}

export function fromNowDate(date: any) {
  return dayjs.utc(date).fromNow();
}
export function UTCDateFormat(date: any) {
  return dayjs.utc(date).format("YYYY-MM-DD HH:mm:ss");
}

export function hmsDateFormat(
  date: DateType,
  formatString: string = "YYYY-MM-DD HH:mm:ss",
) {
  return dayjs(date).format(formatString);
}

export function localDateFormat(date: DateType) {
  return dayjs.utc(date).local().format("YYYY-MM-DD HH:mm:ss");
}

export function getClientTimeZone() {
  return dayjs.tz.guess();
}

function pad(value: any) {
  return value < 10 ? "0" + value : value;
}
export function createOffset(date: any) {
  let sign = date.getTimezoneOffset() > 0 ? "-" : "+";
  let offset = Math.abs(date.getTimezoneOffset());
  let hours = pad(Math.floor(offset / 60));
  let minutes = pad(offset % 60);
  return sign + hours + ":" + minutes;
}

// Map time zone to abbreviation
const timeZoneAbbreviation: any = {
  "Asia/Kolkata": "IST",
  "Asia/Calcutta": "IST",
  "Europe/London": "BST",
  // Add more mappings as needed
};

export function getTimeZone() {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timeZoneAbbreviation[timeZone] || "Unknown";
}

export function dateWithUTCSufix(date: DateType) {
  if (typeof date == "string") {
    date = dayjs(date).toDate();
  }
  return dayjs(date).format("YYYY-MM-DDTHH:mm:ssZ");
}
